import { v4 as uuidv4 } from 'uuid';
import OfflineController from '@/assets/ts/controller/OfflineController';
import SnackbarUtils from "@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils";
import { TableOffline } from "@/assets/ts/interfaces/Offline";
import { SQLHelper } from '@/assets/ts/offline/SQLHelper';

/**
 * A classe DynamicRepository fornece métodos dinâmicos para criar e manipular tabelas SQLite de forma offline.
 * 
 * Métodos:
 * 
 * - `createTables(tables: TableOffline[]): Promise<void>`: Cria tabelas dinamicamente com base nas definições fornecidas na lista de tabelas nome e colunas (DynamicTables.ts).
 * - `getDynamicTable(tableName: string): Promise<any[]>`: Recupera todos os registros de uma tabela específica baseado no nome da tabela.
 * - `setDynamicTableValues(tableName: string, values: any[]): Promise<any>`: Insere registros dinamicamente em uma tabela específica.
 * - `deleteDynamicTable(tableName: string): Promise<any>`: Remove todos os registros de uma tabela específica.
 * - `getTableNames(): Promise<string[]>`: Recupera os nomes de todas as tabelas no banco de dados (SQLite).
 * - `deleteAllTables(): Promise<any>`: Remove todos os registros de todas as tabelas no banco de dados (SQLite).
 * 
 * A classe utiliza `OfflineController` para obter a instância do banco de dados e `SQLHelper` para auxiliar na formatação de colunas e placeholders SQL.
 */
class DynamicRepository {

  // Método para criar tabelas dinamicamente
  public static async createTables(
    tables: TableOffline[]
  ): Promise<void> {
    try {
      const db = OfflineController.getDB();
      await new Promise((resolve, reject) => {
        db.transaction(
          (tx) => {
            tables.forEach((table) => {
              const columns = SQLHelper.formatColumnNamesToCreate(table.columns);
              tx.executeSql(
                `CREATE TABLE IF NOT EXISTS ${table.name} (ID TEXT PRIMARY KEY, ${columns})`
              );
            });
          },
          (error) => {
            reject(error);
          },
          () => {
            resolve('');
          }
        );
      });
    } catch (err) {
      console.log(`Ocoreu um erro ao criar as tabelas necessarias para o Dashboard!: ${err}`);
      SnackbarUtils.showMessage(`Ocoreu um erro ao criar as tabelas necessarias para o Dashboard!: ${err}`, SnackbarUtils.TYPE_ERROR);
      throw err;
    }
  }

  // Método para recuperar todos os registros de uma tabela específica
  public static async getDynamicTable(tableName: string): Promise<any[]> {
    try {

      const db = OfflineController.getDB();
      const table = await new Promise<any[]>((resolve, reject) => {
        db.transaction((tx) => {
          tx.executeSql(`SELECT * FROM ${tableName}`, [], (tx, res) => {
            const tableRow = [];
            for (let i = 0; i < res.rows.length; i++) {
              tableRow.push(res.rows.item(i));
            }
            resolve(tableRow);
          });
        });
      });
      return table;
    } catch (err) {
      throw err;
    }
  }

  // Método para inserir registros dinamicamente em uma tabela específica
  public static async setDynamicTableValues(
    tableName: string,
    values: any[]
  ): Promise<any> {
    try {

      const db = OfflineController.getDB();

      const result = await new Promise((resolve, reject) => {
        db.transaction(
          (tx) => {
            values.forEach((value) => {
                const ID = uuidv4();

                const columns = SQLHelper.getTableColumns(tableName);
                const columnsNames = SQLHelper.formatColumnNames(columns);
                const placeholders = SQLHelper.generateSQLPlaceholders(columns);
                const valuesQuery = SQLHelper.getValuesToQuery(columns, value);
                const finalValues = [ID, ...valuesQuery];

                tx.executeSql(
                  `INSERT INTO ${tableName} (ID, ${columnsNames}) VALUES (${placeholders})`,
                  finalValues
                );
              }
            );
          },
          (error) => {
            reject(error);
          },
          () => {
            resolve([]);
          }
        );
      });

      return result;
    } catch (err) {
      SnackbarUtils.showMessage(err as string, SnackbarUtils.TYPE_ERROR);
      throw err;
    }
  }

  // Método para remover todos os registros de uma tabela específica
  public static async deleteDynamicTable(tableName: string): Promise<any> {
    try {
      const db = OfflineController.getDB();
      const result = await new Promise((resolve, reject) => {
        db.transaction(
          (tx) => {
            tx.executeSql(`DELETE FROM ${tableName}`);
          },
          (error) => {
            reject(error);
          },
          () => {
            resolve('');
          }
        );
      });

      return result;
    } catch (err) {
      SnackbarUtils.showMessage(err as string, SnackbarUtils.TYPE_ERROR);
      throw err;
    }
  }

  // Método para recuperar os nomes de todas as tabelas no banco de dados
  private static async getTableNames(): Promise<string[]> {
    try {

      const db = OfflineController.getDB();
      const table = await new Promise<string[]>((resolve, reject) => {
        db.transaction((tx) => {
          tx.executeSql("SELECT name FROM sqlite_master WHERE type='table'", [], (tx, results) => {
            const names: string[] = [];
            for (let i = 0; i < results.rows.length; i++) {
              names.push(results.rows.item(i).name);
            }
            resolve(names);
          });
        });
      });
      return table;
    } catch (err) {
      throw err;
    }
  }

  // Método para remover todos os registros de todas as tabelas no banco de dados
  public static async deleteAllTables(): Promise<any> {
    try {
      const db = OfflineController.getDB();
      const tableNames = await this.getTableNames();

      const result = await new Promise((resolve, reject) => {
        db.transaction(
          (tx) => {
            tableNames.forEach((tableName) => {
              tx.executeSql(`DELETE FROM ${tableName}`);
            })
          },
          (error) => {
            reject(error);
          },
          () => {
            resolve('');
          }
        );
      });

      return result;
    } catch (err) {
      SnackbarUtils.showMessage(err as string, SnackbarUtils.TYPE_ERROR);
    }
  }
}

export default DynamicRepository;
